import { getLocale, t } from "@travily/i18n";

// i18n-tasks-use t('components.message_time.today')
// i18n-tasks-use t('components.message_time.yesterday')

const MS_PER_DAY = 86_400_000;

/** Whole days between two instants, counted by calendar day rather than elapsed hours, so
 *  23:59 → 00:01 is one day apart and not zero. */
function calendarDaysAgo(date: Date, now: Date): number {
  const then = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((today - then) / MS_PER_DAY);
}

function format(date: Date, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(getLocale(), options).format(date);
}

/** Clock time alone — `2:50 PM`, or `14:50` where the locale prefers it. */
export function formatTimeOfDay(dateString: string): string {
  return format(new Date(dateString), { hour: "numeric", minute: "2-digit" });
}

/**
 * The trailing timestamp on a conversation row, at the coarsest precision that still
 * distinguishes it from its neighbours: a time today, a weekday this week, a date beyond that.
 */
export function formatConversationTimestamp(dateString: string, now = new Date()): string {
  const date = new Date(dateString);
  const days = calendarDaysAgo(date, now);

  if (days <= 0) return formatTimeOfDay(dateString);
  if (days === 1) return t("components.message_time.yesterday");
  if (days < 7) return format(date, { weekday: "short" });
  if (date.getFullYear() === now.getFullYear()) return format(date, { day: "numeric", month: "short" });
  return format(date, { day: "numeric", month: "short", year: "numeric" });
}

/** The separator between days in a thread. Spelled out where the row timestamp abbreviates —
 *  it appears once per day rather than once per row, so it can afford the width. */
export function formatDayLabel(dateString: string, now = new Date()): string {
  const date = new Date(dateString);
  const days = calendarDaysAgo(date, now);

  if (days <= 0) return t("components.message_time.today");
  if (days === 1) return t("components.message_time.yesterday");
  if (days < 7) return format(date, { weekday: "long" });
  if (date.getFullYear() === now.getFullYear()) return format(date, { day: "numeric", month: "long" });
  return format(date, { day: "numeric", month: "long", year: "numeric" });
}

/** Stable key for bucketing messages into days — the local calendar date, not UTC. */
export function dayKey(dateString: string): string {
  const date = new Date(dateString);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}
